import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, styled, Typography } from '@mui/material'
import { useIsSmallScreenFalse } from 'packages/core'
import {
    useAddItemsToCart,
    useCart,
    useConfigSettings,
    useResourceOwnersPaginated,
} from 'hooks'
import { ListingGrid, GridContainer, Filters, Button } from 'components'
import useSubcomponents from 'useSubcomponents'
import { Avatar, Tooltip, useNotification, WarningModal } from 'packages/eid-ui'
import EidTableGrid from 'components/EidTableGrid/EidTableGrid'
import { BulkRemoveButton } from 'components/BulkActionsButtons/BulkRemoveButton'
import { getFullImageUrl, imagesDisabled } from 'utils'
import { Icon } from 'packages/eid-icons'
import { PageContextProvider } from 'pageContext'
import cartHelpers from 'containers/Cart/cartHelpers'
import AddNewAssignment from 'components/Popups/AddNewAssignment/AddNewAssignment'

const DeleteButton = styled(Box)({
    display: 'flex',
    justifyContent: 'end',
    paddingRight: '10px',
    '& button': {
        padding: '8px 12px',
        borderRadius: ' 4px',
        border: 'solid 1px #ff4d5f',
        backgroundColor: '#fff',
    },
})

const canRemoveMultipleMembersControlName =
    'ResourceAdmin-MailboxDetails-AccessManagers-RemoveMultipleMembers-Control'
const canDeleteAccessManagerControlName =
    'ResourceAdmin-MailboxDetails-AccessManagers-DeleteMember-Control'
const canAddNewAccessManagerControlName =
    'ResourceAdmin-MailboxDetails-AccessManagers-AddAccessManagers-Control'
const resourceTypeRoleIdConfigName = 'Mailbox_AccessManagers_ResourceTypeRoleId'

const AccessManagers = ({ mailbox }) => {
    const { t } = useTranslation()
    const { hasAccessToControl, getAccessBasedAttributes } = useSubcomponents()
    const isSmallScreen = useIsSmallScreenFalse()
    const [addItemsToCart, { isLoading: addingToCart }] = useAddItemsToCart()
    const { data: cart } = useCart()
    const { showWarningMessage } = useNotification()
    const [selected, setSelected] = useState([])
    const [selectedItemsToDelete, setSelectedItemsToDelete] = useState(null)

    const {
        list,
        loading: isLoading,
        loadingMore: isLoadingMore,
        pagination,
    } = useResourceOwnersPaginated(mailbox?.id)

    const [getMailboxConfiguration, { isLoading: isConfigLoading }] =
        useConfigSettings(resourceTypeRoleIdConfigName)

    const handleRowSelection = (item) => {
        let selectedItems = [...selected]
        const selectedIndex = selected.findIndex((x) => x.id === item.id)
        if (selectedIndex >= 0) {
            const filteredList = selectedItems.filter((x) => {
                return x.id !== item.id
            })
            setSelected(filteredList)
        } else {
            setSelected([...selectedItems, item])
        }
    }

    const handleSelectAllClick = () => {
        if (selected.length === list.length) {
            setSelected([])
        } else {
            const filteredListNotAssigned = list.filter((item) => {
                if (!checkIfAlreadyAddedToCart(item?.id)) {
                    return item
                }
            })
            if (
                containsAll(filteredListNotAssigned, selected) &&
                containsAll(selected, filteredListNotAssigned)
            ) {
                setSelected([])
            } else {
                setSelected(filteredListNotAssigned)
            }
        }
    }
    const containsAll = (arr1, arr2) =>
        arr2.every((arr2Item) => arr1.includes(arr2Item))

    const checkIfAlreadyAddedToCart = (itemRequestableResourceId) => {
        return cartHelpers.isItemAlreadyInCart(cart, itemRequestableResourceId)
    }

    const handleSingleDelete = (item) => {
        setSelectedItemsToDelete([item])
    }

    const handleDeleteBulk = () => {
        setSelectedItemsToDelete(selected)
    }

    const postAdd = () => {
        setSelectedItemsToDelete(null)
        setSelected([])
    }

    const handleConfirmDelete = () => {
        getMailboxConfiguration(resourceTypeRoleIdConfigName)
            .then((data) => {
                if (data?.value) {
                    const itemsToDelete = selectedItemsToDelete.map((item) => {
                        return {
                            ...item,
                            resourceTypeRoleId_ConfigName: data.value,
                        }
                    })
                    const cartItems = cartHelpers.removeAssignmentToCartItem({
                        targetAssigneeId: mailbox?.id,
                        resource: mailbox,
                        selectedList: itemsToDelete,
                        requestableResourceIdentifier: 'id',
                        resourceTypeRoleIdentifier:
                            'resourceTypeRoleId_ConfigName',
                    })

                    addItemsToCart(cartItems).then(() => postAdd())
                } else {
                    showWarningMessage(t('ErrorDeletingAccessAssignments'))
                }
            })
            .catch((err) => {
                showWarningMessage(t('ErrorDeletingAccessAssignments'))
            })
    }

    const selectAllAction = hasAccessToControl(
        canRemoveMultipleMembersControlName,
    )
        ? {
              render: () => {
                  return (
                      <BulkRemoveButton
                          selectedCount={selected.length}
                          label={t('RemoveSelectedMailboxAccessMembers', {
                              count: selected.length,
                          })}
                          onClick={handleDeleteBulk}
                          dataProtectedsubcomponent={
                              canRemoveMultipleMembersControlName
                          }
                      />
                  )
              },
          }
        : null

    const attributes = [
        {
            label: 'DisplayName',
            name: 'friendlyName',
            sortable: true,
            hideValue: true,
            isHeader: true,
            showInCard: false,
            //NOTE : Keeping the commented out code for subcomponent traceability
            // requireAccess: {
            //     control:
            //         'ResourceAdmin-MailboxDetails-AccessManagers-DisplayName',
            // },
            component: {
                componentName: 'renderer',
                render: (data) => {
                    return (
                        <Box style={{ display: 'flex', gap: 10 }}>
                            {!imagesDisabled && (
                                <Avatar
                                    src={getFullImageUrl(data.imageThumbUrl)}
                                />
                            )}
                            <Typography style={{ fontSize: '13px' }}>
                                {data.friendlyName}
                            </Typography>
                        </Box>
                    )
                },
            },
        },
        {
            hideValue: true,
            showInCard: false,
            showInTable: false,
            image: true,
            component: {
                componentName: 'ResourceImage',
                name: 'imageThumbUrl',
                defaultUrl: '/webcdn/Images/AppLogos/Genric-1.png',
            },
        },
        {
            hideValue: true,
            showInCard: false,
            showInTable: false,
            image: true,
            component: {
                componentName: 'ResourceImage',
                name: 'imageThumbUrl',
                defaultUrl: '/webcdn/Images/AppLogos/Genric-1.png',
            },
        },
        {
            label: 'Login',
            name: 'login',
            sortable: true,

            requireAccess: {
                control: 'ResourceAdmin-MailboxDetails-AccessManagers-Login',
            },
        },

        {
            name: 'email',
            label: 'Email',
            sortable: true,
            requireAccess: {
                control: 'ResourceAdmin-MailboxDetails-AccessManagers-Email',
            },
        },
        {
            name: 'businessRoleLocation',
            label: 'BusinessRoleAndLocation',
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-MailboxDetails-AccessManagers-BusinessRoleAndLocation',
            },
        },
        {
            name: 'department',
            label: 'Department',
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-MailboxDetails-AccessManagers-Department',
            },
        },
        {
            name: 'title',
            label: 'Title',
            sortable: true,
            requireAccess: {
                control: 'ResourceAdmin-MailboxDetails-AccessManagers-Title',
            },
        },

        {
            hideValue: true,
            requireAccess: {
                control: canDeleteAccessManagerControlName,
            },
            component: {
                componentName: 'renderer',
                render: (data) => {
                    return (
                        hasAccessToControl(
                            canDeleteAccessManagerControlName,
                        ) && (
                            <Tooltip
                                title={
                                    checkIfAlreadyAddedToCart(data?.id)
                                        ? t('Common_ItemAlreadyInCart')
                                        : ''
                                }
                            >
                                <DeleteButton>
                                    <Button.Delete
                                        showEndIcon={false}
                                        data-protectedsubcomponent={
                                            canDeleteAccessManagerControlName
                                        }
                                        onClick={() => handleSingleDelete(data)}
                                        disabled={checkIfAlreadyAddedToCart(
                                            data?.id,
                                        )}
                                    />
                                </DeleteButton>
                            </Tooltip>
                        )
                    )
                },
            },
        },
    ]

    return (
        <>
            <ListingGrid>
                <Box
                    display="flex"
                    flexDirection={isSmallScreen ? 'column' : 'row'}
                    alignItems="center"
                    justifyContent="space-between"
                    height="40px"
                >
                    {hasAccessToControl(canAddNewAccessManagerControlName) && (
                        <PageContextProvider
                            key={'AddNewAccessManagerAssignment'}
                        >
                            <AddNewAssignment
                                labelKey={'AddNewAccessManager'}
                                submitButtonLabelKey={'AddNewAccessManager'}
                                searchUrl={`api/people`}
                                targetAssigneeId={mailbox?.id}
                                searchPlaceHolder={'SearchAndFilter'}
                                resourceTypeRoleId_ConfigName={
                                    resourceTypeRoleIdConfigName
                                }
                                startIcon={
                                    <Icon
                                        name={'Plus'}
                                        fill="white"
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            marginRight: '5px',
                                        }}
                                    />
                                }
                            />
                        </PageContextProvider>
                    )}

                    <Box
                        style={{
                            margin: isSmallScreen
                                ? '28px 16px'
                                : '0px 0px 0px auto',
                            width: isSmallScreen ? 'auto' : '308px',
                        }}
                    >
                        <Filters.TextSearch outlined />
                    </Box>
                </Box>
                <GridContainer
                    style={{
                        padding: '16px 0px',
                        marginTop: '0px',
                        backgroundColor: isSmallScreen
                            ? 'white'
                            : 'transparent',
                    }}
                >
                    <EidTableGrid
                        selectAllAction={selectAllAction}
                        rowSelection={hasAccessToControl(
                            canRemoveMultipleMembersControlName,
                        )}
                        handleSelectAllClick={handleSelectAllClick}
                        selected={selected.map((s) => s.id)}
                        handleClick={handleRowSelection}
                        noDataMessage={t('NoDataMessage')}
                        pagination={pagination}
                        isLoading={isLoading}
                        isLoadingMore={isLoadingMore}
                        attributes={getAccessBasedAttributes(attributes)}
                        list={
                            (list?.length &&
                                list.map((item) => {
                                    return {
                                        ...item,
                                        isAlreadyAssigned:
                                            checkIfAlreadyAddedToCart(item?.id),
                                    }
                                })) ||
                            []
                        }
                    />
                </GridContainer>
            </ListingGrid>
            {selectedItemsToDelete && (
                <WarningModal
                    title={
                        selectedItemsToDelete.length === 1
                            ? `${t('DeleteMembers')} ${
                                  selectedItemsToDelete[0]?.friendlyName
                              }`
                            : t('DeleteSelectedItems')
                    }
                    description={t('AppRoleDeleteMessage')}
                    yesLabel={t('Delete')}
                    noLabel={t('Cancel')}
                    open={selectedItemsToDelete}
                    cancel={() => setSelectedItemsToDelete(null)}
                    proceed={handleConfirmDelete}
                    proceeding={addingToCart || isConfigLoading}
                />
            )}
        </>
    )
}

export default AccessManagers
